import React, {Fragment, useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import {Dialog, RadioGroup, Transition} from "@headlessui/react";
import XIcon from "@heroicons/react/solid/XIcon";
import {useTranslation} from "react-i18next";

import {BULK_PURCHASE_PRICE_SCOPE} from "./document-body-bulk-actions-constants";
import {DOCUMENT_EXPORT_OPTIONS_MODAL_Z_INDEX_CLASS} from "../constants";

const DocumentBodyBulkPurchasePriceModal = ({
    isOpen,
    onClose,
    onConfirm,
    isProcessing,
    mode,
    onModeChange,
    selectionCount,
}) => {
    const {t} = useTranslation();

    const handleRequestClose = useCallback(() => {
        if (isProcessing) {
            return;
        }
        onClose();
    }, [isProcessing, onClose]);

    const handleConfirm = useCallback(() => {
        if (isProcessing) {
            return;
        }
        onConfirm();
    }, [isProcessing, onConfirm]);

    const options = useMemo(() => ([
        {
            value: BULK_PURCHASE_PRICE_SCOPE.FILL_MISSING,
            title: t("app.documents.body_mass_actions_purchase_price_scope_missing", {defaultValue: "Aggiorna solo dove il prezzo manca"}),
            description: t("app.documents.body_mass_actions_purchase_price_scope_missing_description", {defaultValue: "Compila esclusivamente le righe senza prezzo o pari a zero, mantenendo invariati i valori già inseriti."}),
        },
        {
            value: BULK_PURCHASE_PRICE_SCOPE.OVERWRITE_ALL,
            title: t("app.documents.body_mass_actions_purchase_price_scope_overwrite", {defaultValue: "Sovrascrivi tutti i prezzi"}),
            description: t("app.documents.body_mass_actions_purchase_price_scope_overwrite_description", {defaultValue: "Aggiorna tutte le righe selezionate, includendo anche i prezzi inseriti manualmente."}),
        },
    ]), [t]);

    const selectionLabel = t("app.documents.body_mass_actions_purchase_price_selection", {
        defaultValue: "{{count}} righe selezionate",
        count: selectionCount,
    });
    const scopeLabel = t("app.documents.body_mass_actions_purchase_price_scope_label", {defaultValue: "Come vuoi applicare l'aggiornamento?"});
    const modalTitle = t("app.documents.body_mass_actions_purchase_price", {defaultValue: "Ricalcola prezzo di acquisto"});
    const modalDescription = t("app.documents.body_mass_actions_purchase_price_modal_description", {defaultValue: "Allinea i prezzi di acquisto delle righe selezionate ai dati più aggiornati del prodotto."});
    const confirmLabel = t("app.documents.body_mass_actions_apply", {defaultValue: "Applica aggiornamento"});

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog
                as="div"
                className={`fixed inset-0 ${DOCUMENT_EXPORT_OPTIONS_MODAL_Z_INDEX_CLASS} overflow-y-auto`}
                onClose={handleRequestClose}
            >
                <div className="min-h-screen px-4 text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-900 bg-opacity-30 transition-opacity" />
                    </Transition.Child>

                    <span className="inline-block h-screen align-middle" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <div className="inline-block w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all">
                            <div className="flex items-center justify-between border-b px-5 py-4">
                                <Dialog.Title className="text-lg font-semibold leading-6 text-blue-1000">
                                    {modalTitle}
                                </Dialog.Title>
                                <button
                                    type="button"
                                    onClick={handleRequestClose}
                                    disabled={isProcessing}
                                    className="rounded-md p-1 text-blue-1000 hover:bg-blue-50 disabled:cursor-not-allowed"
                                >
                                    <span className="sr-only">{t("app.common.close", {defaultValue: "Chiudi"})}</span>
                                    <XIcon className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="px-5 py-5">
                                <p className="text-sm text-gray-600">{modalDescription}</p>
                                <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700">
                                    {selectionLabel}
                                </div>
                                <div className="mt-6">
                                    <p className="text-sm font-semibold text-gray-900">{scopeLabel}</p>
                                    <RadioGroup value={mode} onChange={onModeChange} className="mt-3 space-y-3">
                                        {options.map((option) => (
                                            <RadioGroup.Option
                                                key={option.value}
                                                value={option.value}
                                                disabled={isProcessing}
                                                className={({active, checked}) => {
                                                    const baseClasses = "relative flex cursor-pointer rounded-lg border px-4 py-3 focus:outline-none";
                                                    const stateClasses = checked
                                                        ? "border-blue-1000 bg-blue-50"
                                                        : "border-gray-200 bg-white";
                                                    const ringClasses = active ? "ring-2 ring-blue-1000 ring-opacity-20" : "";
                                                    const disabledClasses = isProcessing ? "opacity-70 cursor-not-allowed" : "";
                                                    return `${baseClasses} ${stateClasses} ${ringClasses} ${disabledClasses}`;
                                                }}
                                            >
                                                {({checked}) => (
                                                    <div className="flex w-full items-start gap-3">
                                                        <div className={`mt-1 flex h-5 w-5 items-center justify-center rounded-full border ${checked ? "border-blue-1000" : "border-gray-300"}`}>
                                                            <span className={`h-2.5 w-2.5 rounded-full ${checked ? "bg-blue-1000" : "bg-transparent"}`} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <RadioGroup.Label as="p" className="text-sm font-semibold text-gray-900">
                                                                {option.title}
                                                            </RadioGroup.Label>
                                                            <RadioGroup.Description as="p" className="text-sm text-gray-600">
                                                                {option.description}
                                                            </RadioGroup.Description>
                                                        </div>
                                                    </div>
                                                )}
                                            </RadioGroup.Option>
                                        ))}
                                    </RadioGroup>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 border-t px-5 py-4">
                                <button
                                    type="button"
                                    onClick={handleRequestClose}
                                    disabled={isProcessing}
                                    className="inline-flex h-9 items-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {t("app.common.cancel", {defaultValue: "Annulla"})}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirm}
                                    disabled={isProcessing}
                                    className="inline-flex h-9 items-center rounded-md bg-blue-1000 px-4 text-sm font-semibold text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                    {isProcessing
                                        ? t("app.common.loading", {defaultValue: "Caricamento..."})
                                        : confirmLabel}
                                </button>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
};

DocumentBodyBulkPurchasePriceModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    isProcessing: PropTypes.bool,
    mode: PropTypes.string.isRequired,
    onModeChange: PropTypes.func.isRequired,
    selectionCount: PropTypes.number,
};

DocumentBodyBulkPurchasePriceModal.defaultProps = {
    isProcessing: false,
    selectionCount: 0,
};

export default DocumentBodyBulkPurchasePriceModal;

